import type { ChannelId } from '@/Enums/ChannelId.enum';
import Color from '@/Enums/Color.enum';
import { ILineItemDuplicate } from '@/Interfaces/CampaignManagement/LineItem.interface';
import type { IControlOption } from '@/Interfaces/ControlOption.interface';
import type { IPackage } from '@/Interfaces/Packages/Package.interface';
import type { IProduct } from '@/Interfaces/Product.interface';
import DateRangeWithShortcuts from '@/components/DateRangeWithShortcuts';
import BigCalendarIcon from '@/components/Icons/BigCalendarIcon';
import ModalBody from '@/components/Modal/common/ModalBody';
import ModalTitleCard from '@/components/Modal/common/ModalTitleCard';
import NumberInput from '@/components/NumberInput';
import { ChannelLabels } from '@/constants/campaignManagement';
import { LINE_ITEM_CHANNEL_OPTIONS } from '@/pages/CampaignManagement/CreateCampaign/LineItemForm/BasicSection/constants';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  selectCampaignManagementProducts,
  selectChannels,
  selectVisibleLineItemChannels,
} from '@/store/selectors/campaignManagement.selectors';
import { selectSharedApiPackages } from '@/store/selectors/sharedApi.selectors';
import { getChannels } from '@/store/slices/campaignManagementSlice';
import type { LineItemChannel } from '@/types/LineItem/channel.types';
import { Add, Clear } from '@mui/icons-material';
import { Autocomplete, Button, FormHelperText, Modal, TextField } from '@mui/material';
import { DateRange } from '@mui/x-date-pickers-pro';
import { Moment } from 'moment';
import { useCallback, useEffect, useMemo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { NEW_LINE_ITEM_DUPLICATE } from './constants';

type LineItemChannelOption = IControlOption<LineItemChannel, ChannelId>;

interface IProps {
  isOpen: boolean;
  handleClose: () => void;
  handleSave: () => void;
  className?: string;
  isLoading?: boolean;
}

const LineItemDuplicationModal = ({ isOpen, handleClose, handleSave, className, isLoading }: IProps) => {
  const {
    setValue,
    control,
    formState: { isValid },
  } = useFormContext();
  const { products: productsWithPublishers } = useAppSelector(selectCampaignManagementProducts);
  const { packages } = useAppSelector(selectSharedApiPackages);
  /**
   * A duplicate targets whatever the source line item does. Orgs on Packages have no Product to
   * inherit, so those rows pick a Package; an org running both keeps the Product dropdown for
   * Product-targeted line items.
   */
  const hasPackages = useMemo<boolean>(() => !!packages?.length, [packages?.length]);

  const channels = useAppSelector(selectChannels);
  const visibleLineItemChannels = useAppSelector(selectVisibleLineItemChannels);

  const dispatch = useAppDispatch();

  const watchDuplicates: ILineItemDuplicate[] = useWatch({ control, name: 'duplicates' });
  const [watchProduct, watchPackageId, watchChannelOption]: [
    IProduct,
    IPackage['id'] | null,
    LineItemChannelOption | null,
  ] = useWatch({
    control,
    name: ['product', 'packageId', 'channel'],
  });
  const watchChannel = watchChannelOption?.label;

  // the modal renders outside the tab switcher, so BasicInfo may never have mounted to fetch these
  useEffect(() => {
    dispatch(getChannels());
  }, [dispatch]);

  const channelOptions = useMemo(
    () =>
      LINE_ITEM_CHANNEL_OPTIONS.filter(option => visibleLineItemChannels.includes(option.value)).reduce<
        LineItemChannelOption[]
      >((options, { value }) => {
        const match = channels?.find(channel => channel.name === value);
        return match ? [...options, { label: value, value: match.id as ChannelId }] : options;
      }, []),
    [visibleLineItemChannels, channels],
  );

  const getChannelProducts = useCallback(
    (channel: LineItemChannel | undefined) => (channel ? productsWithPublishers?.[channel]?.products : null) || [],
    [productsWithPublishers],
  );

  const handleAddDuplicate = useCallback(() => {
    setValue(
      'duplicates',
      [
        ...(watchDuplicates || []),
        {
          ...NEW_LINE_ITEM_DUPLICATE,
          product: watchProduct,
          packageId: watchPackageId,
          channel: watchChannelOption,
        },
      ],
      {
        shouldValidate: true,
        shouldDirty: true,
      },
    );
  }, [setValue, watchDuplicates, watchProduct, watchPackageId, watchChannelOption]);

  const handleRemoveDuplicate = useCallback(
    (index: number) => {
      setValue(
        'duplicates',
        watchDuplicates.filter((_, i) => i !== index),
        { shouldValidate: true },
      );
    },
    [setValue, watchDuplicates],
  );

  const handleSetDuplicateChannel = useCallback(
    (channel: LineItemChannelOption | null, index: number) => {
      setValue(`duplicates[${index}].channel`, channel, { shouldValidate: true, shouldDirty: true });
      setValue(`duplicates[${index}].product`, null, { shouldValidate: true, shouldDirty: true });
    },
    [setValue],
  );

  const handleSetDuplicateDates = useCallback(
    ([startDate, endDate]: DateRange<Moment>, index: number) => {
      setValue(`duplicates[${index}].startDate`, startDate || null, {
        shouldValidate: true,
        shouldDirty: true,
      });
      setValue(`duplicates[${index}].endDate`, endDate || null, {
        shouldValidate: true,
        shouldDirty: true,
      });
    },
    [setValue],
  );

  const LineItemDuplicationModalItem = (index: number) => {
    const duplicateChannel = watchDuplicates[index].channel?.label;
    const movedToAnotherChannel = !!duplicateChannel && duplicateChannel !== watchChannel;

    return (
      <div className='mb-5' key={index}>
        <div className='flex flex-row justify-between gap-1'>
          <div className='w-3/12 h-[48px]'>
            <DateRangeWithShortcuts
              value={[watchDuplicates[index].startDate, watchDuplicates[index].endDate]}
              onChange={range => handleSetDuplicateDates(range, index)}
              timezone='UTC'
              disablePast
              withTime
              inputProps={{
                slotProps: {
                  input: {
                    startAdornment: null,
                    placeholder: 'Select Date Range',
                    sx: { '& input': { paddingRight: 0 } },
                  },
                },
              }}
            />
          </div>
          <div className='w-2/12'>
            <Controller
              name={`duplicates[${index}].channel`}
              control={control}
              render={({ field }) => (
                <Autocomplete<LineItemChannelOption, false, true>
                  {...field}
                  options={channelOptions}
                  isOptionEqualToValue={(option, value) => option.value === value?.value}
                  getOptionKey={option => option.value}
                  getOptionLabel={option => ChannelLabels[option.label]}
                  onChange={(_, data) => handleSetDuplicateChannel(data, index)}
                  sx={{ height: '48px' }}
                  fullWidth
                  renderInput={params => <TextField {...params} placeholder='Select Channel' variant='outlined' />}
                  disableClearable
                />
              )}
            />
          </div>
          <div className='w-3/12'>
            {hasPackages && !watchDuplicates[index].product?.id ? (
              <Controller
                name={`duplicates[${index}].packageId`}
                control={control}
                render={({ field }) => (
                  <Autocomplete<IPackage, false, true>
                    {...field}
                    value={(packages.find(pkg => pkg.id === field.value) ?? null) as IPackage}
                    options={packages}
                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                    getOptionKey={option => option.id}
                    getOptionLabel={option => option.name}
                    onChange={(_, data) => field.onChange(data?.id ?? null)}
                    sx={{ height: '48px' }}
                    fullWidth
                    renderInput={params => <TextField {...params} placeholder='Select Package' variant='outlined' />}
                    disableClearable
                  />
                )}
              />
            ) : (
              <Controller
                name={`duplicates[${index}].product`}
                control={control}
                render={({ field }) => (
                  <Autocomplete<IProduct, false, true>
                    {...field}
                    options={getChannelProducts(duplicateChannel)}
                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                    getOptionKey={option => option.id}
                    getOptionLabel={option => option.name}
                    onChange={(_, data) => field.onChange(data)}
                    sx={{ height: '48px' }}
                    fullWidth
                    renderInput={params => <TextField {...params} placeholder='Select Product' variant='outlined' />}
                    disableClearable
                  />
                )}
              />
            )}
          </div>
          <div className='w-3/12'>
            <Controller
              name={`duplicates[${index}].name`}
              control={control}
              render={({ field }) => (
                <TextField {...field} sx={{ height: '48px' }} fullWidth placeholder='Line Item Name' />
              )}
            />
          </div>
          <div className='w-2/12'>
            <Controller
              name={`duplicates[${index}].impressionsBooked`}
              control={control}
              render={({ field }) => (
                <NumberInput
                  {...field}
                  //@ts-expect-error js component
                  fullWidth
                  placeholder='Line Item Impressions'
                  disableInputArrows
                  NumberInputProps={{
                    thousandsSeparator: ',',
                    unmask: true,
                  }}
                />
              )}
            />
          </div>
          <Button
            variant='contained'
            className='w-1/12 bg-[#1B4332]'
            size={'small'}
            disabled={watchDuplicates.length <= 1}
            sx={{
              height: '48px',
              width: '46px',
              minWidth: '46px',
            }}
            onClick={() => {
              handleRemoveDuplicate(index);
            }}
          >
            <Clear className={'w-6'} />
          </Button>
        </div>
        {movedToAnotherChannel && (
          <FormHelperText sx={{ mt: 1 }}>
            Creatives aren't copied across channels. Associate creatives after saving.
          </FormHelperText>
        )}
      </div>
    );
  };

  return (
    <Modal open={isOpen}>
      <ModalBody
        sx={{ width: '1200px', minHeight: '480px', display: 'flex', flexDirection: 'column', overflow: 'visible' }}
        className={className}
      >
        <div>
          <ModalTitleCard titleIcon={<BigCalendarIcon />} handleClose={handleClose} title='Line Item Duplication' />
          <div className='flex flex-col overflow-y-auto max-h-[600px] mt-8'>
            {watchDuplicates.map((_, index) => LineItemDuplicationModalItem(index))}
          </div>
          <div className='flex flex-row space-x-2'>
            <Button
              className='flex items-center gap-1 font-semibold'
              onClick={handleAddDuplicate}
              sx={{
                fontSize: 14,
                displayPrint: 'none',
                color: Color.Primary,
              }}
            >
              <Add />
              <span>Add Duplicate</span>
            </Button>
          </div>
        </div>
        <Button
          loading={isLoading}
          disabled={!isValid || !watchDuplicates.length}
          variant='contained'
          className='bg-[#1B4332]'
          sx={{
            height: '48px',
            width: '130px',
            marginLeft: 'auto',
          }}
          onClick={handleSave}
          loadingPosition='end'
        >
          Save
        </Button>
      </ModalBody>
    </Modal>
  );
};

export default LineItemDuplicationModal;
